import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Dialog, DialogContent, DialogActions, Button, TextField, MenuItem,
  Autocomplete, CircularProgress, Chip, Stack, IconButton, Typography, Box,
  useMediaQuery, useTheme,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import dayjs from 'dayjs';
import * as actions from '../../../../redux/actions';
import api from '../../../../utils/functions/api';
import { filterMarketingEmployees } from '../../utils/employee';
import { formatEmployeeThaiName } from '../../../../utils/functions/employeeName';
import ModalHeader from '../ui/ModalHeader';

// คลิกขวาที่ cell ในตาราง (OpportunitiesTable.js) แล้วแก้ค่าฟิลด์นั้นตรงๆ โดยไม่ต้องเปิด
// popup รายละเอียดเต็ม — รองรับเฉพาะฟิลด์ที่แก้ได้จริงตาม backend (ไม่รวม เหลืออีกกี่วัน ที่
// เป็นค่าคำนวณ, วันที่สร้าง/วันที่ปิดงาน ที่ระบบตั้งเองเสมอ)
const FIELD_CONFIG = {
  created_by: { label: 'ผู้สร้าง', kind: 'createdBy' },
  contact: { label: 'ลูกค้า', kind: 'contact' },
  // รวมแก้ชื่อโครงการ + ผู้รับผิดชอบ + ผู้ประสานงานในโมดัลเดียว (ตามที่ user ขอ — เดิมผู้รับผิดชอบ/
  // ผู้ประสานงานเคยแยก modal ของตัวเองที่คลิกขวาตรงบรรทัดนั้นๆ ในเซลล์ "โปรเจค" ได้ ตอนนี้คลิกขวา
  // ตรงไหนของเซลล์นี้ก็เด้ง modal นี้ modal เดียวเสมอ ไม่แยกแล้ว)
  project_name: { label: 'ชื่อโครงการ / ผู้รับผิดชอบ / ผู้ประสานงาน', kind: 'projectNameOwner' },
  documentNo: { label: 'Document No.', kind: 'documentNo' },
  detail: { label: 'Work Description', kind: 'textarea', apiKey: 'detail' },
  // Type เลือกจาก popover คลิกขวาเลือกเลยไม่ผ่าน modal นี้แล้ว (เหมือน budget_approve) ดู
  // TypeMenu.js/OpportunitiesTable.js — สถานะก็เลือกตรงๆ ในตารางอยู่แล้วผ่าน <Select> inline
  // ในเซลล์ (คลิกซ้าย, WON พิเศษเปิด FinishJobDialog ผ่าน onStatusChange) เอา onContextMenu ของ
  // เซลล์นี้ออกไปเลย ไม่ต้องมี field kind 'status' ซ้ำซ้อนในนี้อีก
  quotation_due_date: { label: 'Quotation Due Date', kind: 'date', apiKey: 'quotationDueDate' },
  value: { label: 'Quoted Price', kind: 'number', apiKey: 'value' },
  budget: { label: 'Budget', kind: 'number', apiKey: 'budget' },
  // budget_approve เป็น boolean ล้วนๆ (อนุมัติ/ไม่อนุมัติ) — คลิกขวาที่เซลล์นี้ในตารางไม่ผ่าน
  // modal นี้แล้ว ใช้ BudgetApproveMenu.js (popover เลือกแล้วบันทึกทันที) แทน ดู OpportunitiesTable.js
  note: { label: 'Note', kind: 'textarea', apiKey: 'note' },
  appointments: { label: 'วันที่นัด', kind: 'appointments' },
};

function SimpleFieldContent({ config, row, draft, setDraft }) {
  if (config.kind === 'select') {
    return (
      <TextField
        select fullWidth size="small" autoFocus
        value={draft ?? ''}
        onChange={(e) => setDraft(e.target.value)}
      >
        <MenuItem value="">ยังไม่ระบุ</MenuItem>
        {config.options.map((o) => (
          <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
        ))}
      </TextField>
    );
  }
  return (
    <TextField
      fullWidth size="small" autoFocus
      type={config.kind === 'number' ? 'number' : config.kind === 'date' ? 'date' : 'text'}
      multiline={config.kind === 'textarea'}
      minRows={config.kind === 'textarea' ? 3 : undefined}
      value={draft ?? ''}
      onChange={(e) => setDraft(e.target.value)}
      InputLabelProps={config.kind === 'date' ? { shrink: true } : undefined}
    />
  );
}

// ใช้ทั้ง Owner (sourceField='owner_id') และผู้ประสานงาน (sourceField='ordered_by') — สอง field
// นี้เป็น loose reference ไป Mongo Employee เหมือนกันเป๊ะ (คนละ field แต่ keyspace เดียวกัน)
function OwnerFieldContent({
  row, draft, setDraft, sourceField, label,
}) {
  const dispatch = useDispatch();
  const employee = useSelector((state) => state.employee);
  const [inputValue, setInputValue] = useState('');
  const currentId = row[sourceField];

  // ไม่มี name snapshot เก็บไว้ (loose reference ไป Mongo Employee) — fetch ชื่อจริง
  // ตรงๆ ตอนเปิด modal ให้เห็นค่าปัจจุบันก่อนพิมพ์ค้นหาใหม่ (เหมือน useOpportunityDashboard.js)
  useEffect(() => {
    if (!currentId) return;
    api.get(`${process.env.REACT_APP_API_URL}/employee/${currentId}`)
      .then(({ data }) => {
        setDraft({ _id: currentId, name: formatEmployeeThaiName(data) });
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentId]);

  // ดึงพนักงานทั้งหมดครั้งเดียว (ไม่ debounce ค้นหาทีละคำ+size:20 ต่อคำค้นแบบเดิม) — Autocomplete
  // กรอง client-side จาก options เต็มชุดเองอยู่แล้วจาก inputValue
  useEffect(() => {
    dispatch(actions.employeeAll({ size: 9999, page: 1, activeOnly: false }));
  }, [dispatch]);

  const options = useMemo(() => filterMarketingEmployees(employee?.rows).map((e) => ({
    _id: e._id || e.id,
    name: formatEmployeeThaiName(e),
  })), [employee?.rows]);

  return (
    <Autocomplete
      size="small"
      options={options}
      value={draft}
      onChange={(e, v) => setDraft(v)}
      inputValue={inputValue}
      onInputChange={(e, v) => setInputValue(v)}
      getOptionLabel={(o) => o?.name || ''}
      isOptionEqualToValue={(o, v) => (o?._id || o?.id) === (v?._id || v?.id)}
      renderInput={(params) => (
        // eslint-disable-next-line react/jsx-props-no-spreading
        <TextField {...params} autoFocus label={label} />
      )}
    />
  );
}

// แก้ชื่อโครงการ + ผู้รับผิดชอบ + ผู้ประสานงานพร้อมกันในโมดัลเดียว — draft เป็น
// { projectName, owner, orderedBy } รวมกัน (ต่างจาก field อื่นที่ draft เป็นค่าเดี่ยว) reuse
// OwnerFieldContent ตัวเดิมทั้งสองจุด โดย wrap setDraft ให้อัปเดตแค่ key ของตัวเอง ไม่ทับที่เหลือทิ้ง
function ProjectNameOwnerFieldContent({ row, draft, setDraft }) {
  return (
    <Stack spacing={2}>
      <TextField
        fullWidth size="small" autoFocus multiline minRows={3}
        label="ชื่อโครงการ"
        value={draft?.projectName ?? ''}
        onChange={(e) => setDraft({ ...draft, projectName: e.target.value })}
      />
      <OwnerFieldContent
        row={row}
        draft={draft?.owner}
        setDraft={(owner) => setDraft((prev) => ({ ...prev, owner }))}
        sourceField="owner_id"
        label="ผู้รับผิดชอบ"
      />
      <OwnerFieldContent
        row={row}
        draft={draft?.orderedBy}
        setDraft={(orderedBy) => setDraft((prev) => ({ ...prev, orderedBy }))}
        sourceField="ordered_by"
        label="ผู้ประสานงาน"
      />
    </Stack>
  );
}

// ผู้สร้าง (created_by) เก็บ User._id (auto จาก req.user.id ตอนสร้าง) — คนละ keyspace จาก
// owner_id/ordered_by ที่เก็บ Employee._id ตรงๆ ต้อง match ผ่าน employee.uid (Employee ที่ผูกกับ
// User คนนั้น) แทน — employee ที่ไม่มี uid (ไม่เคยผูก user login) จะไม่โผล่เป็นตัวเลือกให้เลือก
function CreatorFieldContent({ row, draft, setDraft }) {
  const dispatch = useDispatch();
  const employee = useSelector((state) => state.employee);
  const [inputValue, setInputValue] = useState('');
  const matchedRef = React.useRef(false);

  useEffect(() => {
    dispatch(actions.employeeAll({ size: 9999, page: 1, activeOnly: false }));
  }, [dispatch]);

  const options = useMemo(() => (employee?.rows || [])
    .filter((e) => e.uid)
    .map((e) => ({ _id: e.uid, name: formatEmployeeThaiName(e) })),
  [employee?.rows]);

  // เติมชื่อจริงให้ draft ว่างๆ ({_id, name:''} ที่ CellEditModal ตั้งไว้ก่อน) ทันทีที่ options
  // โหลดมาครบ — ทำครั้งเดียว (matchedRef กันโดนพิมพ์ค้นหาทับกลับไปเป็นชื่อเดิม)
  useEffect(() => {
    if (matchedRef.current || !row.created_by || options.length === 0) return;
    const match = options.find((o) => o._id === row.created_by);
    if (match) setDraft(match);
    matchedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options, row.created_by]);

  return (
    <Autocomplete
      size="small"
      options={options}
      value={draft}
      onChange={(e, v) => setDraft(v)}
      inputValue={inputValue}
      onInputChange={(e, v) => setInputValue(v)}
      getOptionLabel={(o) => o?.name || ''}
      isOptionEqualToValue={(o, v) => (o?._id || o?.id) === (v?._id || v?.id)}
      renderInput={(params) => (
        // eslint-disable-next-line react/jsx-props-no-spreading
        <TextField {...params} autoFocus label="ผู้สร้าง" />
      )}
    />
  );
}

function ContactFieldContent({ draft, setDraft }) {
  const dispatch = useDispatch();
  const contact = useSelector((state) => state.contact);
  const [inputValue, setInputValue] = useState('');

  // ดึงลูกค้าทั้งหมดครั้งเดียว (ไม่ debounce ค้นหาทีละคำ+size:20 ต่อคำค้น) — Autocomplete
  // กรอง client-side จาก options เต็มชุดเองอยู่แล้วจาก inputValue (เหตุผลเดียวกับ OwnerFieldContent
  // ด้านบน/useOpportunityDashboard.js — เดิมเปิดเลือกแล้วเห็นลูกค้าไม่ครบ)
  useEffect(() => {
    dispatch(actions.contactAll({ contactType: 'client', size: 9999, page: 1 }));
  }, [dispatch]);

  const options = useMemo(() => contact?.rows || [], [contact?.rows]);

  return (
    <Autocomplete
      size="small"
      options={options}
      value={draft}
      onChange={(e, v) => setDraft(v)}
      inputValue={inputValue}
      onInputChange={(e, v) => setInputValue(v)}
      getOptionLabel={(o) => o?.name || ''}
      isOptionEqualToValue={(o, v) => (o?._id || o?.id) === (v?._id || v?.id)}
      renderInput={(params) => (
        // eslint-disable-next-line react/jsx-props-no-spreading
        <TextField {...params} autoFocus label="ลูกค้า" error={!draft} helperText={!draft ? 'กรุณาเลือกลูกค้า' : ' '} />
      )}
    />
  );
}

// แก้ Code + เลขที่เอกสารพร้อมกันในโมดัลเดียว (opportunity_number = code + "_" + yy + xxxx —
// ดู opportunity.repository.js) — yy (ปีที่สร้างจริง) ไม่ให้แก้ตรงนี้ คงไว้เหมือนเดิมเสมอ
function DocumentNoFieldContent({ draft, setDraft, yy }) {
  const codeRefs = React.useRef([]);
  const xxxRefs = React.useRef([]);
  const codeValid = /^[A-Za-z]{2}$/.test(draft?.code || '');
  const xxxValid = /^[0-9]{4}$/.test(draft?.xxx || '');

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75, fontWeight: 600 }}>
          Code * (2 ตัว เช่น MS)
        </Typography>
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
          {Array.from({ length: 2 }, (_, index) => (
            <TextField
              key={`code-${index}`}
              size="small"
              autoFocus={index === 0}
              inputRef={(el) => { codeRefs.current[index] = el; }}
              value={draft?.code?.[index] || ''}
              onChange={(e) => {
                const value = e.target.value.toUpperCase().replace(/[^A-Z]/g, '').slice(-1);
                const code = (draft?.code || '').split('');
                code[index] = value;
                setDraft({ ...draft, code: code.join('').slice(0, 2) });
                if (value && index < 1) codeRefs.current[index + 1]?.focus();
              }}
              inputProps={{ maxLength: 1, style: { textAlign: 'center', textTransform: 'uppercase' } }}
              sx={{ width: 44 }}
            />
          ))}
        </Stack>
      </Box>
      <Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75, fontWeight: 600 }}>
          เลขที่เอกสาร * (4 หลัก เช่น 0035)
        </Typography>
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
          {Array.from({ length: 4 }, (_, index) => (
            <TextField
              key={`xxx-${index}`}
              size="small"
              inputRef={(el) => { xxxRefs.current[index] = el; }}
              value={draft?.xxx?.[index] || ''}
              onChange={(e) => {
                const value = e.target.value.replace(/[^0-9]/g, '').slice(-1);
                const xxx = (draft?.xxx || '').split('');
                xxx[index] = value;
                setDraft({ ...draft, xxx: xxx.join('').slice(0, 4) });
                if (value && index < 3) xxxRefs.current[index + 1]?.focus();
              }}
              inputProps={{ maxLength: 1, inputMode: 'numeric', style: { textAlign: 'center' } }}
              sx={{ width: 40 }}
            />
          ))}
        </Stack>
      </Box>
      <Typography variant="caption" color={codeValid && xxxValid ? 'text.secondary' : 'error.main'}>
        {codeValid && xxxValid
          ? `Document No.: ${draft.code}_${yy}${draft.xxx}`
          : 'กรุณากรอก Code 2 ตัวและเลขที่เอกสาร 4 หลักให้ครบ'}
      </Typography>
    </Stack>
  );
}

function AppointmentsFieldContent({ row, onSaved }) {
  const dispatch = useDispatch();
  const [list, setList] = useState(row.marketing_appointments || []);
  const [date, setDate] = useState('');
  const [title, setTitle] = useState('');
  const [busy, setBusy] = useState(false);

  const add = async () => {
    if (!date) return;
    setBusy(true);
    try {
      const created = await dispatch(actions.opportunityAddAppointment({ id: row.id, date, title: title.trim() || undefined }));
      setList((p) => [...p, created].sort((a, b) => new Date(a.date) - new Date(b.date)));
      setDate('');
      setTitle('');
      onSaved();
    } finally {
      setBusy(false);
    }
  };

  const remove = async (appointmentId) => {
    setList((p) => p.filter((a) => a.id !== appointmentId));
    await dispatch(actions.opportunityRemoveAppointment({ id: row.id, appointmentId }));
    onSaved();
  };

  return (
    <Box>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
        {list.length === 0 && <Typography variant="body2" color="text.secondary">ยังไม่มีวันที่นัด</Typography>}
        {list.map((a) => (
          <Chip
            key={a.id || a.date}
            label={a.title ? `${dayjs(a.date).format('DD/MM/YYYY')} — ${a.title}` : dayjs(a.date).format('DD/MM/YYYY')}
            onDelete={() => remove(a.id)}
            deleteIcon={<DeleteOutlineIcon />}
            size="small"
          />
        ))}
      </Stack>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
        <TextField
          size="small" type="date" value={date}
          onChange={(e) => setDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ flex: '1 1 150px' }}
        />
        <TextField
          size="small" label="นัดเรื่องอะไร" value={title}
          onChange={(e) => setTitle(e.target.value)}
          sx={{ flex: '2 1 180px' }}
        />
        <IconButton color="primary" onClick={add} disabled={!date || busy}>
          <AddIcon />
        </IconButton>
      </Stack>
    </Box>
  );
}

export function CellEditModal({
  open, row, fieldKey, onClose, onSaved,
}) {
  const dispatch = useDispatch();
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const config = fieldKey ? FIELD_CONFIG[fieldKey] : null;
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    if (!open || !row || !config) return;
    if (config.kind === 'createdBy') {
      setDraft(row.created_by ? { _id: row.created_by, name: '' } : null);
    } else if (config.kind === 'projectNameOwner') {
      setDraft({
        projectName: row.project_name || '',
        owner: row.owner_id ? { _id: row.owner_id, name: '' } : null,
        orderedBy: row.ordered_by ? { _id: row.ordered_by, name: '' } : null,
      });
    } else if (config.kind === 'contact') {
      // contact_name_snapshot เก็บมากับ opportunity อยู่แล้ว (ต่างจาก owner) เลยเติมชื่อได้ทันที
      // ไม่ต้อง fetch แยก
      setDraft(row.contact_id ? { _id: row.contact_id, name: row.contact_name_snapshot || '' } : null);
    } else if (config.kind === 'documentNo') {
      setDraft({ code: row.code || '', xxx: (row.number || '').slice(2, 6) || '' });
    } else if (config.kind === 'date') {
      const raw = row[fieldKey];
      setDraft(raw ? dayjs(raw).format('YYYY-MM-DD') : '');
    } else if (fieldKey === 'note') {
      // note มีประวัติแยกเก็บทุกครั้งที่บันทึกอยู่แล้ว (marketing_note_history, ดูการ์ด
      // "ประวัติการบันทึก Note" ในหน้ารายละเอียด) — เปิด modal นี้แต่ละครั้งจึงเป็นการ "เพิ่ม note
      // ใหม่" ไม่ใช่แก้ไขของเก่า เริ่มจากค่าว่างเสมอ ไม่ prefill ค่าเดิม กันคนแก้ทับ note เก่าเงียบๆ
      setDraft('');
    } else if (config.apiKey) {
      const raw = row[fieldKey];
      setDraft(raw == null ? '' : String(raw));
    }
  }, [open, row, fieldKey]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!open || !row || !config) return null;

  const documentNoInvalid = config?.kind === 'documentNo'
    && !(/^[A-Za-z]{2}$/.test(draft?.code || '') && /^[0-9]{4}$/.test(draft?.xxx || ''));
  const contactInvalid = config?.kind === 'contact' && !draft;

  const handleSave = async () => {
    if (documentNoInvalid || contactInvalid) {
      return;
    }
    setSaving(true);
    try {
      if (config.kind === 'createdBy') {
        await dispatch(actions.updateOpportunity({ id: row.id, createdBy: draft ? (draft._id || draft.id) : null }));
      } else if (config.kind === 'projectNameOwner') {
        await dispatch(actions.updateOpportunity({
          id: row.id,
          projectName: draft.projectName,
          ownerId: draft.owner ? (draft.owner._id || draft.owner.id) : null,
          orderedById: draft.orderedBy ? (draft.orderedBy._id || draft.orderedBy.id) : null,
        }));
      } else if (config.kind === 'contact') {
        await dispatch(actions.updateOpportunity({
          id: row.id, contactId: draft._id || draft.id, contactNameSnapshot: draft.name,
        }));
      } else if (config.kind === 'documentNo') {
        await dispatch(actions.updateOpportunity({ id: row.id, code: draft.code, xxx: draft.xxx }));
      } else if (config.kind === 'number') {
        const trimmed = String(draft ?? '').trim();
        const value = trimmed === '' ? undefined : Number(trimmed);
        await dispatch(actions.updateOpportunity({ id: row.id, [config.apiKey]: value }));
      } else {
        await dispatch(actions.updateOpportunity({ id: row.id, [config.apiKey]: draft || undefined }));
      }
      onSaved();
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const isAppointments = config.kind === 'appointments';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={fullScreen}
      PaperProps={{ sx: { borderRadius: fullScreen ? 0 : 3 } }}
    >
      <ModalHeader
        icon={<EditOutlinedIcon fontSize="small" />}
        title={fieldKey === 'note' ? 'เพิ่ม note' : `แก้ไข ${config.label}`}
        subtitle={row.project_name}
        onClose={onClose}
        disabled={saving}
      />
      <DialogContent sx={{ p: 3 }}>
        {config.kind === 'createdBy' && <CreatorFieldContent row={row} draft={draft} setDraft={setDraft} />}
        {config.kind === 'projectNameOwner' && <ProjectNameOwnerFieldContent row={row} draft={draft} setDraft={setDraft} />}
        {config.kind === 'contact' && <ContactFieldContent draft={draft} setDraft={setDraft} />}
        {config.kind === 'documentNo' && (
          <DocumentNoFieldContent draft={draft} setDraft={setDraft} yy={(row.number || '').slice(0, 2)} />
        )}
        {isAppointments && <AppointmentsFieldContent row={row} onSaved={onSaved} />}
        {!isAppointments && !['createdBy', 'projectNameOwner', 'contact', 'documentNo'].includes(config.kind) && (
          <SimpleFieldContent config={config} row={row} draft={draft} setDraft={setDraft} />
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
        <Button onClick={onClose} variant="outlined" sx={{ borderRadius: 2 }} disabled={saving}>
          {isAppointments ? 'ปิด' : 'ยกเลิก'}
        </Button>
        {!isAppointments && (
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={saving || documentNoInvalid || contactInvalid}
            sx={{ borderRadius: 2 }}
          >
            {saving ? <CircularProgress size={18} color="inherit" /> : 'บันทึก'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default CellEditModal;
